import * as NR from "@newrelic/browser-agent";
import { CandidateSessionInfoContext } from "../types/telemetry/types";
import { NR_EVENTS } from "../types/telemetry/events";

class NewRelicService {
  private agent?: NR.BrowserAgent;
  private initialized = false;

  constructor() {
    if (process.env.REACT_APP_USE_NEW_RELIC !== "true") return;

    try {
      this.agent = new NR.BrowserAgent({
        info: {
          applicationID: process.env.REACT_APP_NR_APPLICATION_ID ?? "",
          beacon: "bam.nr-data.net",
          errorBeacon: "bam.nr-data.net",
          licenseKey: process.env.REACT_APP_NR_LICENSE_KEY ?? "",
        },
        init: {
          distributed_tracing: { enabled: true },
          privacy: { cookies_enabled: true },
          session_replay: {
            enabled: true,
            autoStart: true,
            sampling_rate: 100,
            error_sampling_rate: 100,
            mask_all_inputs: true,
            mask_text_selector: "*",
          },
          ajax: {
            deny_list: ["bam.nr-data.net"],
          },
        },
        loader_config: {
          accountID: process.env.REACT_APP_NR_ACCOUNT_ID,
          agentID: process.env.REACT_APP_NR_AGENT_ID,
          applicationID: process.env.REACT_APP_NR_APPLICATION_ID,
          licenseKey: process.env.REACT_APP_NR_LICENSE_KEY,
          trustKey: process.env.REACT_APP_NR_TRUST_KEY,
        },
      });
      this.agent.start();
      this.agent.recordReplay?.();
      this.initialized = true;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("NewRelic init failed", err);
    }

    if (this.agent) {
      const fetchAndSetIp = async () => {
        try {
          const res = await fetch("https://api.ipify.org?format=json", {
            cache: "no-store",
          });
          if (!res.ok) throw new Error(`ipify http ${res.status}`);
          const { ip } = await res.json();
          this.agent?.setCustomAttribute("PublicIp", ip);
          this.agent?.setCustomAttribute("PublicIpUpdatedAt", Date.now());
          
          this.trackEvent(NR_EVENTS.IP_FETCHED, { publicIp: ip, timestamp: Date.now() });

          return ip;
        } catch (err) {
          this.logError("Failed to fetch public IP for New Relic:", err);
          return null;
        }
      };

      fetchAndSetIp();
      window.setInterval(fetchAndSetIp, 60_000);
    }
  }

  setUser(id?: string) {
    if (!id) return;
    this.agent?.setUserId(`candidate-${id}`);
  }

  setCandidateSessionInfoContext(context: CandidateSessionInfoContext) {
    if (!this.initialized || !this.agent) return;

    this.agent.setCustomAttribute(
      "ConfirmationNumber",
      context.confirmationNumber ?? "",
    );
    this.agent.setCustomAttribute(
      "EligibilityCode",
      context.eligibilityCode ?? "",
    );
    this.agent.setCustomAttribute("ExamId", context.examId ?? "");
    this.agent.setCustomAttribute("ExamDate", context.examDate ?? "");
    this.agent.setCustomAttribute("ExamCode", context.examCode ?? "");
    this.agent.setCustomAttribute("ProgramCode", context.programCode ?? "");
    this.agent.setCustomAttribute("ClientCode", context.clientCode ?? "");
    this.agent.setCustomAttribute("DeliveryMode", context.deliveryMode ?? "");
    this.agent?.setUserId(context.confirmationNumber ?? "");

    if (context.address) {
      this.agent.setCustomAttribute("ScheduledCity", context.address?.city ?? "");
      this.agent.setCustomAttribute(
        "ScheduledStateProvince",
        context.address?.stateProvince ?? "",
      );
      this.agent.setCustomAttribute(
        "ScheduledCountryName",
        context.address?.country?.name ?? "",
      );
      this.agent.setCustomAttribute(
        "ScheduledCountryCode",
        context.address?.country?.isoCode ?? "",
      );
    }

    this.trackEvent(NR_EVENTS.CONTEXT_INITIALIZED);
  }

  trackLoginSuccess() {
    this.trackEvent(NR_EVENTS.CANDIDATE_INPUT);
  }

  trackEvent(
    name: string,
    attributes?: Record<string, string | number | boolean>,
  ) {
    if (!this.initialized || !this.agent) return;
    this.agent.addPageAction?.(name, attributes);
  }

  logError(message: string, error?: unknown) {
    if (!this.initialized || !this.agent) return;

    this.agent.noticeError?.(
      error instanceof Error ? error : new Error(message),
      { message },
    );

    this.trackEvent(NR_EVENTS.UI_ERROR, { message });
  }
}

export const NewRelic = new NewRelicService();
